import { ecId, record, upper, type Row } from '../ability/context.js';
import { heldInventoryRows } from './gm-boundary.js';
import { statusRemovalBefore, statusRows, type ExpiryReaders, type StatusRemoval } from './expiry.js';
import { applyStatusFrame } from '../turns/status-frame.js';
import { emptySpawnPlan, type SpawnRowPlan } from '../spawn/row-plan.js';

/** Bind the shared expiry walk to the held inventory graph. Definitions and untouched rows stay borrowed. */
export function withInventoryExpiry<T extends ExpiryReaders>(read: T, encounter: Row, roster: Row[]): T {
  const inventory = heldInventoryRows({ encounter, members: new Map(roster.map(row => [ecId(row), row])) });
  return { ...read, inventoryRows: () => [...inventory.values()],
    rowFor: id => read.rowFor(id) ?? inventory.get(upper(id)) ?? null,
    allStatusRows: () => [...read.allStatusRows(), ...[...inventory.values()].flatMap(row => [...statusRows(row, 'target'), ...statusRows(row, 'caster')])] };
}

/** Existing time/tick typed-row saver for non-creature holders; timing is decided only by expirableStatuses. */
export function inventoryExpiryPlan(removals: readonly StatusRemoval[], read: ExpiryReaders): SpawnRowPlan {
  const plan = emptySpawnPlan();
  for (const removal of removals) {
    const row = read.inventoryRows?.().find(row => statusRows(row, 'target').some(s => upper(s.status_id) === removal.status_id));
    const status = row && statusRows(row, 'target').find(s => upper(s.status_id) === removal.status_id);
    if (!status) continue;
    const before = statusRemovalBefore(status);
    delete before.status_caster_id; // Shared typed status tombstones bind target/item/connection.
    plan.writes.push({ table: 'statuses', op: 'update', row: { status_id: removal.status_id, status_deleted: 1 }, before });
  }
  return plan;
}

/** Apply the SAME status ids delivered by turn frames or typed time rows, touching only matching inventory buckets. */
export function applyInventoryExpiry(encounter: Row, roster: Row[], ids: ReadonlySet<string>, definition?: (id: string) => Row | null): void {
  if (!ids.size) return;
  for (const row of heldInventoryRows({ encounter, members: new Map(roster.map(row => [ecId(row), row])) }).values()) {
    const removed = [...statusRows(row, 'target'), ...statusRows(row, 'caster')].filter(s => ids.has(upper(s.status_id))).map(s => upper(s.status_id));
    if (removed.length) applyStatusFrame(row, { status_remove_ids: [...new Set(removed)], skills: {} },
      id => definition?.(id) ?? record(record(encounter.catalog)?.[id]));
  }
}
